import FeatureToggles, {
    FeatureToggleKeys,
} from "@services/featureToggles.services";
import { AnalyticsEvent } from "@services/tracking/types/analyticsEvent";

import EventDataLayer from "../../queue/EventDataLayer";
import { SubscriberCallback } from "../../queue/SubscriberCallback";
import { getGoogleAnalyticsAddToCartImpression } from "./mappers/GoogleAnalyticsAddToCartImpression";
import { getGoogleAnalyticsAddToWishlistImpression } from "./mappers/GoogleAnalyticsAddToWishlistImpression";
import { getGoogleAnalyticsBannerClick } from "./mappers/GoogleAnalyticsBannerClick";
import { getGoogleAnalyticsBannerImpression } from "./mappers/GoogleAnalyticsBannerImpression";
import { getGoogleAnalyticsButtonClick } from "./mappers/GoogleAnalyticsButtonClick";
import { getGoogleAnalyticsButtonImpression } from "./mappers/GoogleAnalyticsButtonImpression";
import { getGoogleAnalyticsCartImpression } from "./mappers/GoogleAnalyticsCartImpression";
import {
    getGoogleAnalyticsCheckoutPurchase,
    getGoogleAnalyticsCheckoutStep,
} from "./mappers/GoogleAnalyticsCheckoutStep";
import { getGoogleAnalyticsLinkClick } from "./mappers/GoogleAnalyticsLinkClick";
import { getGoogleAnalyticsNavigationClick } from "./mappers/GoogleAnalyticsNavigationLinkClick";
import { getGoogleAnalyticsPageImpression } from "./mappers/GoogleAnalyticsPageImpression";
import { getGoogleAnalyticsProductClickImpression } from "./mappers/GoogleAnalyticsProductClickImpression";
import { getGoogleAnalyticsProductDetailImpression } from "./mappers/GoogleAnalyticsProductDetailImpression";
import { handleGoogleAnalyticsProductImpression } from "./mappers/GoogleAnalyticsProductImpression";
import { getGoogleAnalyticsRemoveFromCartImpression } from "./mappers/GoogleAnalyticsRemoveFromCartImpression";
import { getGoogleAnalyticsSectionImpression } from "./mappers/GoogleAnalyticsSectionImpression";
import {
    getGoogleAnalyticsToolStep,
    getGoogleAnalyticsToolStepSubmit,
} from "./mappers/GoogleAnalyticsToolStep";

const ensureDataLayer = () => {
    if (!window.dataLayer) window.dataLayer = [];
};

const GoogleAnalyticsSubscriber = (
    eventDataLayer: EventDataLayer,
    withHistory = false,
) => {
    ensureDataLayer();
    eventDataLayer.subscribe(
        "google-analytics-subscriber",
        subscribe,
        withHistory,
    );
};

const subscribe: SubscriberCallback = (
    event: string,
    data: AnalyticsEvent,
): void => {
    let gtmData;

    switch (event) {
        case "page-impression":
            gtmData = getGoogleAnalyticsPageImpression(data);
            break;
        case "section-impression":
            gtmData = getGoogleAnalyticsSectionImpression(data);
            break;
        case "product-detail-impression":
            gtmData = getGoogleAnalyticsProductDetailImpression(data);
            break;
        case "product-impression":
            gtmData = handleGoogleAnalyticsProductImpression(data);
            break;
        case "product-click":
            gtmData = getGoogleAnalyticsProductClickImpression(data);
            break;
        case "add-to-cart":
            gtmData = getGoogleAnalyticsAddToCartImpression(data);
            break;
        case "remove-from-cart":
            gtmData = getGoogleAnalyticsRemoveFromCartImpression(data);
            break;
        case "add-to-wishlist":
            gtmData = getGoogleAnalyticsAddToWishlistImpression(data);
            break;
        case "cart-view":
            gtmData = getGoogleAnalyticsCartImpression(data);
            break;
        case "tool-step":
            if (data.payload.eventData.name === "checkout") {
                gtmData = getGoogleAnalyticsCheckoutStep(data);
            } else {
                gtmData = getGoogleAnalyticsToolStep(data);
            }
            break;
        case "tool-laststep":
            if (data.payload.eventData.name === "checkout") {
                gtmData = getGoogleAnalyticsCheckoutPurchase(data);
            } else {
                gtmData = getGoogleAnalyticsToolStepSubmit(data);
            }
            break;
        case "button-impression":
            gtmData = getGoogleAnalyticsButtonImpression(data);
            break;
        case "button-click":
            gtmData = getGoogleAnalyticsButtonClick(data);
            break;
        case "banner-click":
            gtmData = getGoogleAnalyticsBannerClick(data);
            break;
        case "banner-impression":
            gtmData = getGoogleAnalyticsBannerImpression(data);
            break;
        case "link-click":
            gtmData = getGoogleAnalyticsLinkClick(data);
            break;
        case "navigation-click":
            gtmData = getGoogleAnalyticsNavigationClick(data);
            break;

        default:
            if (FeatureToggles.isEnabled(FeatureToggleKeys.ANALYTICS_LOGGING)) {
                console.info("hubo:google-analytics - untracked event", event);
            }
    }

    if (gtmData) {
        window.dataLayer.push(gtmData);

        if (FeatureToggles.isEnabled(FeatureToggleKeys.ANALYTICS_LOGGING)) {
            console.groupCollapsed(
                `hubo:google-analytics - event`,
                gtmData.event,
            );
            console.log(gtmData);
            console.groupEnd();
        }
    }
};

export { GoogleAnalyticsSubscriber };
